import React, { useState } from 'react';

// Interfața pentru contribuitor
interface Contributor {
  id: number;
  nume: string;
  avatar: string;
  companie: string;
  puncte: number;
  puncte_anul_curent: number;
  puncte_luna_curenta: number;
  activitati: string[];
  nivel: string;
  evenimente_participare: number;
}

const TopContributors: React.FC = () => {
  // State pentru perioada selectată
  const [selectedPeriod, setSelectedPeriod] = useState<'luna' | 'an' | 'mereu'>('luna');
  
  // Lista de contribuitori
  const contributori: Contributor[] = [
    {
      id: 1,
      nume: "Maria Popescu",
      avatar: "MP",
      companie: "Vinex SRL",
      puncte: 3750,
      puncte_anul_curent: 1250,
      puncte_luna_curenta: 450,
      activitati: ["Încărcare documente", "Inițiere proiecte", "Comentarii", "Sponsorizări"],
      nivel: "Platină",
      evenimente_participare: 12
    },
    {
      id: 2,
      nume: "Ion Marinescu",
      avatar: "IM",
      companie: "TechSoft Vrancea",
      puncte: 2860,
      puncte_anul_curent: 1820,
      puncte_luna_curenta: 380,
      activitati: ["Încărcare documente", "Comentarii", "Sponsorizări"],
      nivel: "Aur",
      evenimente_participare: 8
    },
    {
      id: 3,
      nume: "Elena Vasilescu",
      avatar: "EV",
      companie: "Agro Solutions",
      puncte: 2350,
      puncte_anul_curent: 1050,
      puncte_luna_curenta: 340,
      activitati: ["Încărcare documente", "Inițiere proiecte", "Comentarii"],
      nivel: "Aur",
      evenimente_participare: 7
    },
    {
      id: 4,
      nume: "Adrian Munteanu",
      avatar: "AM",
      companie: "Mondial Construct",
      puncte: 4200,
      puncte_anul_curent: 980,
      puncte_luna_curenta: 180,
      activitati: ["Inițiere proiecte", "Sponsorizări", "Organizare evenimente"],
      nivel: "Platină",
      evenimente_participare: 15
    },
    {
      id: 5,
      nume: "Cristina Dumitru",
      avatar: "CD",
      companie: "Eco Farm",
      puncte: 1950,
      puncte_anul_curent: 1400,
      puncte_luna_curenta: 320,
      activitati: ["Încărcare documente", "Comentarii", "Sponsorizări"],
      nivel: "Argint",
      evenimente_participare: 5
    },
    {
      id: 6,
      nume: "Mihai Ionescu",
      avatar: "MI",
      companie: "IT Solutions",
      puncte: 3100,
      puncte_anul_curent: 1600,
      puncte_luna_curenta: 280,
      activitati: ["Inițiere proiecte", "Comentarii", "Mentorare"],
      nivel: "Platină",
      evenimente_participare: 10
    },
    {
      id: 7,
      nume: "Ana Popovici",
      avatar: "AP",
      companie: "Marketing Pro",
      puncte: 2200,
      puncte_anul_curent: 1100,
      puncte_luna_curenta: 290,
      activitati: ["Încărcare documente", "Comentarii", "Organizare evenimente"],
      nivel: "Aur",
      evenimente_participare: 8
    },
    {
      id: 8,
      nume: "George Stanescu",
      avatar: "GS",
      companie: "Logistică Vrancea",
      puncte: 1800,
      puncte_anul_curent: 900,
      puncte_luna_curenta: 250,
      activitati: ["Sponsorizări", "Comentarii"],
      nivel: "Argint",
      evenimente_participare: 6
    },
    {
      id: 9,
      nume: "Laura Georgescu",
      avatar: "LG",
      companie: "Beauty Salon Elegant",
      puncte: 1650,
      puncte_anul_curent: 850,
      puncte_luna_curenta: 220,
      activitati: ["Încărcare documente", "Comentarii"],
      nivel: "Argint",
      evenimente_participare: 4
    },
    {
      id: 10,
      nume: "Florin Popa",
      avatar: "FP",
      companie: "Construcții FP",
      puncte: 2450,
      puncte_anul_curent: 1200,
      puncte_luna_curenta: 230,
      activitati: ["Inițiere proiecte", "Sponsorizări", "Mentorare"],
      nivel: "Aur",
      evenimente_participare: 9
    }
  ];

  // Sortam contributorii in functie de perioada selectata
  const getContributorsSorted = () => {
    return [...contributori].sort((a, b) => {
      if (selectedPeriod === 'luna') {
        return b.puncte_luna_curenta - a.puncte_luna_curenta;
      } else if (selectedPeriod === 'an') {
        return b.puncte_anul_curent - a.puncte_anul_curent;
      } else {
        return b.puncte - a.puncte;
      }
    });
  };

  // Reguli de punctare
  const reguli_punctare = [
    { activitate: "Încărcare document", puncte: 50, descriere: "Per document relevant încărcat și aprobat" },
    { activitate: "Inițiere proiect de crowdfunding", puncte: 200, descriere: "Pentru fiecare proiect lansat" },
    { activitate: "Comentariu util", puncte: 10, descriere: "Pentru comentarii constructive la propuneri" },
    { activitate: "Sponsorizare", puncte: "variabil", descriere: "1 punct pentru fiecare 10 lei contribuiți" },
    { activitate: "Propunere votată", puncte: 20, descriere: "Când propunerea primește 10+ voturi" },
    { activitate: "Organizare eveniment", puncte: 300, descriere: "Pentru organizarea unui eveniment comunitar" },
    { activitate: "Mentorare", puncte: 100, descriere: "Per antreprenor mentorat (lunar)" },
  ];

  // Niveluri de contribuitor și beneficii
  const niveluri = [
    { 
      nume: "Bronz", 
      puncte_necesare: 500, 
      beneficii: [
        "Invitații la evenimente mici",
        "Acces la biblioteca de documente de bază"
      ]
    },
    { 
      nume: "Argint", 
      puncte_necesare: 1500, 
      beneficii: [
        "Toate beneficiile nivelului Bronz",
        "Invitații la evenimente medii",
        "Acces la sesiuni de networking dedicate",
        "Badge special pe platformă"
      ]
    },
    { 
      nume: "Aur", 
      puncte_necesare: 2500, 
      beneficii: [
        "Toate beneficiile nivelului Argint",
        "Invitații la toate evenimentele",
        "Acces la sesiuni exclusive cu antreprenori de succes",
        "Promovare pe site-ul și materialele Patronatului",
        "Reduceri la servicii partenere"
      ]
    },
    { 
      nume: "Platină", 
      puncte_necesare: 3500, 
      beneficii: [
        "Toate beneficiile nivelului Aur",
        "Invitații VIP la evenimente naționale",
        "Oportunitatea de a fi speaker la evenimente",
        "Mentorat personalizat",
        "Prioritate la inițiativele Patronatului",
        "Acces la clubul exclusiv al membrilor platină"
      ]
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Top Contribuitori</h2>
        <p className="text-gray-600">Membrii cei mai activi din comunitatea Patronatului Vrancea</p>
      </div>

      {/* Secțiunea cu explicații despre sistemul de puncte */}
      <div className="p-6 bg-blue-50 mb-6">
        <h3 className="text-lg font-semibold mb-3 text-blue-800">Sistem de Contribuții și Recompense</h3>
        <p className="mb-4">
          La Patronatul Vrancea, activitatea și implicarea ta în comunitate sunt recompensate. Pentru fiecare contribuție pe care o aduci - fie că încarci documente utile, sponsorizezi un proiect, participi activ la discuții sau inițiezi proiecte noi - primești puncte care îți cresc statutul în comunitate.
        </p>
        <p className="mb-4">
          În funcție de punctele acumulate, poți atinge diferite niveluri de membru, fiecare cu beneficii specifice. Membrii cei mai activi sunt invitați la evenimente exclusive organizate de Patronatul Vrancea și beneficiază de oportunități unice de networking și dezvoltare.
        </p>
        <p className="text-sm text-blue-700 italic">
          Contribuie activ și devino un membru de elită al comunității noastre!
        </p>
      </div>

      {/* Tab selector pentru perioada */}
      <div className="p-4 border-b">
        <div className="flex space-x-4">
          <button
            className={`px-4 py-2 rounded-md font-medium ${selectedPeriod === 'luna' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setSelectedPeriod('luna')}
          >
            Ultimele 30 de zile
          </button>
          <button
            className={`px-4 py-2 rounded-md font-medium ${selectedPeriod === 'an' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setSelectedPeriod('an')}
          >
            Anul curent
          </button>
          <button
            className={`px-4 py-2 rounded-md font-medium ${selectedPeriod === 'mereu' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setSelectedPeriod('mereu')}
          >
            Totdeauna
          </button>
        </div>
      </div>

      {/* Lista de contribuitori */}
      <div className="p-4">
        <div className="grid grid-cols-1 gap-4">
          {getContributorsSorted().map((contribuitor, index) => (
            <div key={contribuitor.id} className="border rounded-lg p-4 hover:shadow-md transition-all">
              <div className="flex items-center">
                <div className="flex-shrink-0 mr-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold text-white
                    ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-yellow-700' : 'bg-blue-500'}`}>
                    {index < 3 ? (index + 1) : contribuitor.avatar}
                  </div>
                  {index < 3 && (
                    <div className="text-center mt-1 font-semibold text-sm">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{contribuitor.nume}</h3>
                      <p className="text-gray-600 text-sm">{contribuitor.companie}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {contribuitor.activitati.map((activitate, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-gray-100 text-xs rounded-md">
                            {activitate}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedPeriod === 'luna' 
                          ? contribuitor.puncte_luna_curenta 
                          : selectedPeriod === 'an' 
                            ? contribuitor.puncte_anul_curent 
                            : contribuitor.puncte} 
                        <span className="text-sm ml-1">puncte</span>
                      </div>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1
                        ${contribuitor.nivel === 'Platină' ? 'bg-purple-100 text-purple-800' : 
                          contribuitor.nivel === 'Aur' ? 'bg-yellow-100 text-yellow-800' :
                          contribuitor.nivel === 'Argint' ? 'bg-gray-100 text-gray-800' : 'bg-yellow-900 text-white'}`}>
                        Nivel {contribuitor.nivel}
                      </span>
                      <div className="text-sm text-gray-500 mt-1">
                        {contribuitor.evenimente_participare} evenimente
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sistem de puncte detaliat */}
      <div className="p-4 border-t">
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Cum obții puncte</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activitate</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puncte</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalii</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reguli_punctare.map((regula, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{regula.activitate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{regula.puncte}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{regula.descriere}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Nivele și beneficii */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Niveluri și beneficii</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {niveluri.map((nivel, index) => (
              <div key={index} className={`border rounded-lg p-4
                ${nivel.nume === 'Bronz' ? 'border-yellow-900' : 
                  nivel.nume === 'Argint' ? 'border-gray-400' : 
                  nivel.nume === 'Aur' ? 'border-yellow-500' : 'border-purple-500'}`}>
                <div className={`text-center p-2 rounded-md mb-3 font-bold
                  ${nivel.nume === 'Bronz' ? 'bg-yellow-900 text-white' : 
                    nivel.nume === 'Argint' ? 'bg-gray-400 text-white' : 
                    nivel.nume === 'Aur' ? 'bg-yellow-500 text-white' : 'bg-purple-500 text-white'}`}>
                  Nivel {nivel.nume}
                </div>
                <div className="text-center mb-3 font-semibold">{nivel.puncte_necesare}+ puncte</div>
                <ul className="space-y-1 text-sm">
                  {nivel.beneficii.map((beneficiu, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>{beneficiu}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* CTA */}
      <div className="p-6 bg-gray-50 border-t text-center">
        <h3 className="text-lg font-semibold mb-2">Implică-te în comunitatea Patronatului Vrancea</h3>
        <p className="text-gray-600 mb-4">
          Cu cât contribui mai mult, cu atât beneficiezi de mai multe oportunități și conexiuni valoroase.
        </p>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium">
          Vezi oportunități de contribuție
        </button>
      </div>
    </div>
  );
};

export default TopContributors;